import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, merge } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface MachineReservationDialogData {
  reservation?: any;
  prefillMachineId?: number;
  prefillProjectId?: number;
}

const dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const start = control.get('start_date')?.value;
  const end = control.get('end_date')?.value;
  if (start && end && end < start) {
    return { dateRange: true };
  }
  return null;
};

@Component({
  selector: 'app-machine-reservation-form-dialog',
  templateUrl: './machine-reservation-form-dialog.component.html',
  styleUrls: ['./machine-reservation-form-dialog.component.css'],
})
export class MachineReservationFormDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  editMode = false;
  saving = false;
  checking = false;
  machines: any[] = [];
  projects: any[] = [];
  staff: any[] = [];
  conflicts: any[] = [];
  available: boolean | null = null;

  statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'in_use', label: 'In use' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  private destroy$ = new Subject<void>();
  private checkSeq = 0;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private toastr: ToastrMsgService,
    public dialogRef: MatDialogRef<MachineReservationFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MachineReservationDialogData
  ) {}

  ngOnInit(): void {
    const r = this.data.reservation;
    this.editMode = !!r;

    this.form = this.fb.group(
      {
        machine_id: [r?.machine_id || this.data.prefillMachineId || '', Validators.required],
        project_id: [r?.project_id || this.data.prefillProjectId || ''],
        operator_staff_id: [r?.operator_staff_id || ''],
        title: [r?.title || '', Validators.required],
        purpose: [r?.purpose || ''],
        start_date: [this.formatDateInput(r?.start_date) || '', Validators.required],
        end_date: [this.formatDateInput(r?.end_date) || '', Validators.required],
        status: [r?.status || 'confirmed', Validators.required],
        notes: [r?.notes || ''],
      },
      { validators: dateRangeValidator }
    );

    this.loadLookups();

    if (this.canCheckAvailability) {
      this.checkAvailability(true);
    }

    merge(
      this.form.get('machine_id')!.valueChanges,
      this.form.get('start_date')!.valueChanges,
      this.form.get('end_date')!.valueChanges
    )
      .pipe(
        tap(() => {
          this.available = null;
          this.conflicts = [];
        }),
        debounceTime(400),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.canCheckAvailability) {
          this.checkAvailability(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedMachine(): any | null {
    const id = this.form?.get('machine_id')?.value;
    if (!id) return null;
    return this.machines.find((m) => String(m.id) === String(id)) || null;
  }

  get datesTouched(): boolean {
    return !!(this.form.get('start_date')?.touched || this.form.get('end_date')?.touched);
  }

  get canCheckAvailability(): boolean {
    if (!this.form) return false;
    const { machine_id, start_date, end_date } = this.form.value;
    return !!(machine_id && start_date && end_date && !this.form.hasError('dateRange'));
  }

  get durationLabel(): string {
    const start = this.form?.get('start_date')?.value;
    const end = this.form?.get('end_date')?.value;
    if (!start || !end || end < start) return '';
    const days = this.inclusiveDays(start, end);
    return days === 1 ? '1 day' : `${days} days`;
  }

  get availabilityTitle(): string {
    if (this.checking) return 'Checking availability';
    if (this.available === true) return 'Machine is available';
    if (this.available === false) return 'Scheduling conflict';
    if (this.canCheckAvailability) return 'Ready to check';
    return 'Select a schedule';
  }

  get availabilityHint(): string {
    if (this.checking) return 'Looking for overlapping reservations…';
    if (this.available === true) {
      return this.durationLabel
        ? `Free for the selected ${this.durationLabel.toLowerCase()}.`
        : 'Free for the selected dates.';
    }
    if (this.available === false) return 'This machine is already booked on overlapping dates.';
    if (this.canCheckAvailability) return 'Availability will be checked automatically.';
    return 'Pick a machine and both dates to verify availability.';
  }

  loadLookups(): void {
    this.api.getMachineMaster().subscribe({
      next: (res: any) => {
        this.machines = Array.isArray(res) ? res : res?.data || [];
      },
    });
    this.api.getProjectCommonAll({ limit: 500 }).subscribe({
      next: (res: any) => {
        this.projects = res?.data || [];
      },
    });
    this.api.getStaff({ keyword: '' }).subscribe({
      next: (res: any) => {
        this.staff = res?.data || [];
      },
    });
  }

  checkAvailability(silent = false): void {
    if (!this.canCheckAvailability) return;

    const { machine_id, start_date, end_date } = this.form.value;
    const seq = ++this.checkSeq;
    this.checking = true;
    const params: any = { machine_id, start_date, end_date };
    if (this.editMode && this.data.reservation?.id) {
      params.exclude_id = this.data.reservation.id;
    }

    this.api.checkMachineAvailability(params).subscribe({
      next: (res: any) => {
        if (seq !== this.checkSeq) return;
        this.checking = false;
        this.available = res.available;
        this.conflicts = res.conflicts || [];
        if (!silent && !res.available) {
          this.toastr.error(res.reason || 'Machine not available for selected dates');
        }
      },
      error: (err) => {
        if (seq !== this.checkSeq) return;
        this.checking = false;
        this.toastr.error(err?.error?.message || 'Availability check failed');
      },
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.available === false) {
      return;
    }

    this.saving = true;
    const payload = { ...this.form.value };
    if (!payload.project_id) payload.project_id = null;
    if (!payload.operator_staff_id) payload.operator_staff_id = null;

    const req = this.editMode
      ? this.api.updateMachineReservation(this.data.reservation.id, payload)
      : this.api.createMachineReservation(payload);

    req.subscribe({
      next: (res: any) => {
        this.saving = false;
        this.toastr.success(res.message || 'Reservation saved');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        const body = err?.error;
        if (body?.error === 'CONFLICT') {
          this.conflicts = body.conflicts || [];
          this.available = false;
          this.toastr.error(body.message || 'Scheduling conflict');
        } else {
          this.toastr.error(body?.message || 'Failed to save reservation');
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  machineLabel(m: any): string {
    return `${m.machine_name || 'Machine'} (${m.machine_id || m.id})`;
  }

  machineHint(m: any): string {
    const parts = [
      m.machine_category || m.machineCategory,
      m.machine_type || m.machineType,
      m.operational_status || m.operationalStatus,
    ].filter(Boolean);
    return parts.join(' · ');
  }

  private inclusiveDays(start: string, end: string): number {
    const a = this.parseLocalDate(start);
    const b = this.parseLocalDate(end);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
  }

  private parseLocalDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private formatDateInput(value: string | Date | null | undefined): string {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
